import API from '@/services/http'
import { getExpressList } from '../express'
import type * as Types from './types'

/**
 * 地址校验
 * @param payload
 * @returns
 */
export const addressVerify = (payload: Types.VerifyType) => {
  return API.admin.post<Types.IVerifyResponseType>('/api/address/verify', payload)
}

/** 地址新增 */
export const addressAdd = async (params: Types.AddressAddParams) => {
  return API.admin.post('/api/user/address/add', params)
}

/** 获取服务产品列表 */
export const fetchExpressList = async (params: Types.FetchExpressListParams) => {
  const response = await getExpressList({
    ...params,
    pageSize: 999, // 临时
    needStationType: true,
  })

  const { bizCode, bizMessage, message, data } = response
  if (bizCode === 'SUCCESS') {
    const expresses = data?.data || []
    return expresses
  }

  return Promise.reject({ bizCode, bizMessage, message })
}

/** 获取收货地址区域列表 */
export const fetchAreaList = async (params: Types.FetchRecipientAddressAreaParams) => {
  const response = await API.admin.post<{ nodes: Types.Area[] }>('/api/address/list', params)
  const { data, bizCode, bizMessage, message } = response
  if (bizCode === 'SUCCESS') {
    const { nodes: areas } = data
    return areas
  }

  return Promise.reject({ bizCode, bizMessage, message })
}

/** 获取门店地址列表 */
export const fetchStoreAreaList = async (params: Types.FetchStationAreaListParams) => {
  const response = await API.admin.post<{ areaNodes: Types.StoreAreaList }>('/api/station/list_child', params)
  const { bizCode, data, bizMessage, message } = response
  if (bizCode === 'SUCCESS') {
    return data.areaNodes || []
  }

  return Promise.reject({ bizCode, bizMessage, message })
}

/** 获取门店列表 | 收货地址 - 店配 */
export const fetchStationList = async (payload: Types.FetchStationListParams) => {
  const params = Object.assign({ pageSize: 20, pageNum: 1 }, payload || {})
  const response = await API.admin.post<Types.FetchStationListResp>('/api/station/list', params)
  const { bizCode, data, bizMessage, message } = response
  if (bizCode === 'SUCCESS') {
    return data
  }

  return Promise.reject({ bizCode, bizMessage, message })
}

/** 获取过滤无效门店后的门店列表 - 店配 */
export const fetchStationListFilter = async (params: Types.FetchStationListParams) => {
  const response = await API.admin.post<Types.FetchStationListResp>('/api/sales/order_query/list_station', params)
  const { bizCode, data, bizMessage, message } = response
  if (bizCode === 'SUCCESS') {
    return data
  }

  return Promise.reject({ bizCode, bizMessage, message })
}

/** 获取支持的方式 */
export const fetchSupportMethods = async (params: Types.FetchSupportMethodsParams) => {
  const { departureCountryCode, arrivalCountryCode } = params
  const resp = await fetchExpressList({
    senderAddressName: {
      countryCode: departureCountryCode,
    },
    consigneeAddressName: {
      countryCode: arrivalCountryCode,
    },
    needStationType: true,
    queryDeliveryDate: true,
  })

  const stringifySupportedMethods = resp?.map((item) => item.supportedAddressType)?.join(',') || ''
  const methodsMap: Record<string, Types.AddressTypeValues> = { HOME_STREET: 'home', STORE_STREET: 'store' }

  const supportedMethods: Types.AddressTypeValues[] = []
  Object.keys(methodsMap).forEach((name) => {
    if (-1 !== stringifySupportedMethods.indexOf(name)) {
      supportedMethods.push(methodsMap[name])
    }
  })

  return supportedMethods
}

/** 获取地图 url */
export const fetchMapUrl = (params: Types.FetchMapUrlParams) => {
  const { mapType = 'TCAT_1', ...restParams } = params
  return API.admin.post<{ url: string }>('/api/emap/get_url', { mapType, ...restParams })
}

/** 地址列表,根据国家和发件或收件查询 */
export const getAddressList = (params: { pageSize?: number; pageNum?: number; countryCode: string; addressType: number }) => {
  return API.admin.post<Types.AddressListState>(
    '/api/user/address/list',
    Object.assign(
      {
        pageNum: 1,
        pageSize: 10,
      },
      params || {}
    )
  )
}

export const getAddressListApi = async (data) => await API.admin.post('/api/user/address/list', data)

/** 通过addressNo获取地址 */
export const getAddressListByNos = async (addressNos: string[]) => {
  const response = await API.admin.post('/api/user/address/get_multi', {
    addressNos,
  })

  const { bizCode, bizMessage: message, data } = response
  if (bizCode === 'SUCCESS') {
    return { data, bizCode, message }
  }

  return Promise.reject({ bizCode, message })
}

/**
 * **接口** [批量地址校验接口↗](http://api-doc-sl.inshopline.com/#/project/74/feature%2F20220811_998_vnaddr4/interface/49608ac20e763fc982d03c2389964b39)
 * @请求头 `POST /api/address/batch-verify`
 * @更新时间 `2022-07-19 14:46:35`
 */
export function postBatchVerify(data: Types.OneshipBatchAddressVerifyRequest) {
  return API.admin.post<Types.BatchVerifyRes>('/api/address/batch_verify', data)
}

/**
 * 多语言国家列表-分页
 */
export const countryPage = (params: Types.Page) =>
  API.admin.post<{
    data: Types.CountryItem[]
  }>('/api/country/page', params)

/** 地址智能识别 */
export const getAddressAutoSplitResult = async (params: Types.AutoSplitAddressReq[], sceneId?: string) => {
  const { data } = await API.admin.post<{ output: Types.AutoSplitAddressType[] }>('/api/address-service/split/smart-split', { input: params, sceneId })
  return data?.output || []
  // const mockData = {
  //   countryCode: 'HK',
  //   name: 'Ceci',
  //   areaCode: '0086',
  //   phoneNum: null,
  //   province: 'New Territories',
  //   city: 'North',
  //   district: 'Sheung Shui',
  //   town: '',
  //   address: '香港南区利乐街533号',
  //   zipCode: '999077',
  //   email: null,
  //   code: 200,
  //   msg: 'success',
  // }
  // return Promise.resolve(
  //   params.map((i, ind) => ({
  //     ...mockData,
  //     metadataId: String(ind),
  //   }))
  // )
}

/** 获取支持拆分算法的国家 */
export const getSupportAutoSplitCountryList = async () => {
  const { data } = await API.admin.post<{ items: Types.SplitSupportCountryType[] }>('/api/address-service/split/support?_sw_cache=15')
  return data?.items
}
